import React, { FunctionComponent, useState, useContext } from "react";
import {
  Alert,
  Button,
  Choice,
  Close,
  HelpDrawerToggle,
  HelpDrawer,
} from "@cmsgov/ds-medicare-gov";
import {
  BooleanMap,
  ActionType,
  PharmacyType,
  PlanType,
  DrugCoverageFilterOption,
  PlanCategoryFilters,
} from "../../@types";
import {
  CONSTANTS as C,
  sendTealiumEvent,
  doFiltersExist,
} from "../../helpers";
import CarrierFilter from "./CarrierFilter";
import CoverageFilterGroup from "./CoverageFilterGroup";
import className from "classnames";
import { useTranslate } from "../../helpers";
import { ChangeLocation, PrintButton } from "../../components";
import StarRatingsFilter from "./StarRatingsFilter";
import { AppStore } from "../../app/store";
import { defaultState } from "../../app/store/AppStore";
import { Link } from "react-router-dom";
import routes from "../../app/routes";
import DrugCoverageFilter from "./DrugCoverageFilter";
import CategoryFilterGroup from "./CategoryFilterGroup";
import { useFlags } from "launchdarkly-react-client-sdk";

/* istanbul ignore next: just an interface definition */
export interface PlanFiltersContainerProps {
  heading?: React.ReactNode;
  resetToFirstPageOfResults: () => void;
  numPlans: number;
  loadingPlans: boolean;
  availableCategories?: PlanCategoryFilters;
  showSeniorSavingsModelFilter: boolean;
}

const PlanFiltersContainer: FunctionComponent<PlanFiltersContainerProps> = ({
  availableCategories,
  resetToFirstPageOfResults,
  showSeniorSavingsModelFilter,
  ...props
}) => {
  // Tracks whether the filter area should be open or closed
  const [open, setOpen] = useState(false);
  const [
    seniorSavingsModelDrawerOpen,
    setSeniorSavingsModelDrawerOpen,
  ] = useState(false);
  const t = useTranslate();
  const {
    state: {
      searchResultsFilters = defaultState.searchResultsFilters,
      pharmacyType,
      prescriptions,
      planType,
    },
    dispatch,
  } = useContext(AppStore.AppContext);
  const { tmpSeniorSavingsModel } = useFlags();

  const {
    drugCoverageFilter,
    filters,
    insuranceCarrier,
    planCategoryFilters,
    starRatingFilter,
    seniorSavingsModelFilter,
  } = searchResultsFilters;

  const clearAllFilters = (): void => {
    dispatch({
      type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
      payload: defaultState.searchResultsFilters,
    });
    resetToFirstPageOfResults();
  };

  const convertFilterKeysToLabels = (
    filters: BooleanMap
  ): { key: string; label: string }[] => {
    return Object.keys(filters).map(filter => ({
      key: filter,
      label: t(C.filterLabelsByValue[filter]),
    }));
  };

  const filterLabels = convertFilterKeysToLabels(filters);

  const handleFilterRemove = (filter: string): void => {
    const newFilters = { ...filters, [filter]: false };
    const filtersToSend = Object.entries(newFilters).filter(([_, val]) => val);
    const selectedFilters = filtersToSend.reduce(
      (selections, [filter, selected]) => {
        selections[filter] = selected;

        return selections;
      },
      {} as BooleanMap
    );

    dispatch({
      type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
      payload: {
        ...searchResultsFilters,
        filters: selectedFilters,
      },
    });

    resetToFirstPageOfResults();
  };

  /* istanbul ignore next: tested elsewhere */
  const OpenFilterButton = (mobile = false): JSX.Element => (
    <Button
      variation="primary"
      onClick={(): void => {
        // istanbul ignore next: tested elsewhere
        if (!open) {
          sendTealiumEvent("Find Plans - Available Plans", "Filter Plans");
        }
        setOpen(!open);
      }}
      className={`PlanFilters__displayButton PlanFilters__displayButton--${
        mobile ? "mobile" : "desktop"
      } e2e-results-filter mct-hide-print`}
      aria-expanded={open}
      aria-controls="plan-filters-container"
    >
      {t("search_results_page.filter_plans")}
    </Button>
  );

  const filtersExist = doFiltersExist(searchResultsFilters);
  /* istanbul ignore next: tested elsewhere */
  const filtersAreActive = !props.loadingPlans && filtersExist;

  const SelectedFilters: FunctionComponent<{ alignRight?: boolean }> = ({
    alignRight,
  }) => (
    <div className="mct-hide-print">
      {!filtersExist && (
        <div
          className={`PlanFilters__noFilters ${
            alignRight ? "ds-u-text-align--right" : ""
          }`}
        >
          {t("filter_labels.none_selected")}
        </div>
      )}

      {filtersExist && (
        <ul
          className={`PlanFilters__activeFilters ${
            alignRight ? "ds-u-justify-content--end" : ""
          }`}
        >
          {filterLabels.map(filter => (
            <li key={filter.key}>
              <Button
                className="ds-c-button--primary-outline ds-c-button-icon--small"
                size="small"
                onClick={(): void => handleFilterRemove.call(null, filter.key)}
              >
                <span className="sr-only">
                  {t("filter_labels.applied_aria")}
                </span>
                {filter.label}
                <Close />
                <span className="sr-only">{t("filter_labels.aria")}</span>
              </Button>
            </li>
          ))}

          {insuranceCarrier && (
            <li key={insuranceCarrier}>
              <Button
                className="ds-c-button--primary-outline ds-c-button-icon--small"
                size="small"
                onClick={(): void => {
                  dispatch({
                    type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                    payload: {
                      ...searchResultsFilters,
                      insuranceCarrier: "",
                    },
                  });
                  resetToFirstPageOfResults();
                }}
              >
                <span className="sr-only">
                  {t("filter_labels.applied_aria")}
                </span>
                {insuranceCarrier}
                <Close />
                <span className="sr-only">{t("filter_labels.aria")}</span>
              </Button>
            </li>
          )}

          {!!starRatingFilter.length && (
            <li key={starRatingFilter}>
              <Button
                className="ds-c-button--primary-outline ds-c-button-icon--small"
                size="small"
                onClick={(): void => {
                  dispatch({
                    type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                    payload: {
                      ...searchResultsFilters,
                      starRatingFilter: "",
                    },
                  });
                  resetToFirstPageOfResults();
                }}
              >
                <span className="sr-only">
                  {t("filter_labels.applied_aria")}
                </span>
                {starRatingFilter}{" "}
                {Number(starRatingFilter) === 5
                  ? t("plan_terms.stars")
                  : t("plan_terms.stars_and_up")}{" "}
                <Close />
                <span className="sr-only">{t("filter_labels.aria")}</span>
              </Button>
            </li>
          )}

          {drugCoverageFilter && (
            <li key={drugCoverageFilter}>
              <Button
                className="ds-c-button--primary-outline ds-c-button-icon--small"
                size="small"
                onClick={(): void => {
                  dispatch({
                    type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                    payload: {
                      ...searchResultsFilters,
                      drugCoverageFilter: undefined,
                    },
                  });
                  resetToFirstPageOfResults();
                }}
              >
                <span className="sr-only">
                  {t("filter_labels.applied_aria")}
                </span>
                {drugCoverageFilter === DrugCoverageFilterOption.INCLUDES
                  ? t("search_results_page.includes_drug_coverage")
                  : t("search_results_page.excludes_drug_coverage")}
                <Close />
                <span className="sr-only">{t("filter_labels.aria")}</span>
              </Button>
            </li>
          )}

          {planCategoryFilters.map(cat => {
            return (
              <li key={cat}>
                <Button
                  className="ds-c-button--primary-outline ds-c-button-icon--small"
                  size="small"
                  onClick={(): void => {
                    dispatch({
                      type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                      payload: {
                        ...searchResultsFilters,
                        planCategoryFilters: planCategoryFilters.filter(
                          f => f !== cat
                        ),
                      },
                    });
                    resetToFirstPageOfResults();
                  }}
                >
                  <span className="sr-only">
                    {t("filter_labels.applied_aria")}
                  </span>
                  {t(C.categoryFilters[cat])}
                  <Close />
                  <span className="sr-only">{t("filter_labels.aria")}</span>
                </Button>
              </li>
            );
          })}
          {tmpSeniorSavingsModel && seniorSavingsModelFilter && (
            <li>
              <Button
                className="ds-c-button--primary-outline ds-c-button-icon--small"
                size="small"
                onClick={(): void => {
                  dispatch({
                    type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                    payload: {
                      ...searchResultsFilters,
                      seniorSavingsModelFilter: false,
                    },
                  });
                  resetToFirstPageOfResults();
                }}
              >
                <span className="sr-only">
                  {t("filter_labels.applied_aria")}
                </span>
                <span>{t("filter_labels.senior_savings_model")}</span>
                <span className="sr-only">{t("filter_labels.aria")}</span>
                <Close />
              </Button>
            </li>
          )}
        </ul>
      )}
    </div>
  );

  return (
    <div className="PlanFilters">
      <div className="PlanFilters__header">
        <div>{!props.loadingPlans && props.heading}</div>
        <div className="mct-hide-print">
          <PrintButton
            tealiumProps={{
              event_action: "Find Plans - Available Plans",
              event_label: "Print",
            }}
          />
        </div>
      </div>

      <div className="ds-u-lg-display--flex ds-u-lg-justify-content--between">
        <div>
          <ChangeLocation
            showLocation={true}
            tealiumOpenProps={{
              event_action: "Find Plans - Available Plans",
              event_label: "Change Location",
            }}
            tealiumSaveProps={{
              event_action: "Find Plans - Available Plans",
              event_label: "Save Location",
            }}
          />
          <Link
            className="SearchResults__blueLink ds-u-font-weight--bold ds-u-display--block ds-u-margin-top--2 mct-hide-print"
            to={
              prescriptions.length
                ? routes.prescriptionsList
                : routes.managePrescriptions
            }
            onClick={(): void => {
              sendTealiumEvent(
                "Find Plans - Available Plans",
                "Edit Your Drugs and Pharmacies"
              );

              if (
                [PharmacyType.NONE, PharmacyType.MAIL].includes(pharmacyType)
              ) {
                dispatch({
                  type: ActionType.UPDATE_PHARMACY_TYPE,
                  payload: PharmacyType.MAIL_AND_RETAIL,
                });
              }
            }}
          >
            {t("search_results_page.edit_drugs_and_pharmacies")}
          </Link>
        </div>
        <div className="ds-u-display--none ds-u-lg-display--block mct-u-width--50">
          <div className="ds-u-display--flex ds-u-justify-content--end ds-u-margin-y--2">
            {OpenFilterButton()}
          </div>
          <SelectedFilters alignRight={true} />
        </div>
      </div>

      {props.numPlans === 0 && filtersAreActive && (
        <Alert
          className="PlanFilters__noPlansWarning animated fadeIn"
          variation="warn"
          heading={t("search_results_page.no_plans_available")}
        >
          {t("search_results_page.no_plans_with_filters")}{" "}
          <Button
            variation="transparent"
            className="ds-c-button--primary-alt ds-u-padding--0"
            onClick={(): void => {
              clearAllFilters();
            }}
          >
            {t("search_results_page.clear_all_filters")}
          </Button>{" "}
          {t("search_results_page.remove_filters_individually")}
        </Alert>
      )}

      {props.numPlans === 0 && !filtersAreActive && !props.loadingPlans && (
        <Alert
          className="PlanFilters__noPlansWarning animated fadeIn"
          variation="warn"
          heading={t("search_results_page.no_plans_available")}
        >
          {t("search_results_page.no_plans_for_area")} <ChangeLocation inline />{" "}
          {t("search_results_page.change_loc_to_find_plans")}
        </Alert>
      )}

      <div className="ds-u-margin-top--2 ds-u-md-margin-top--4 ds-u-lg-display--none mct-hide-print">
        {OpenFilterButton(true)}

        <SelectedFilters />
      </div>

      <div
        id="plan-filters-container"
        className={className(
          "PlanFilters__container",
          !open && "PlanFilters__container--hidden"
        )}
      >
        <div className="PlanFilters__filterGroups">
          <CoverageFilterGroup onFilterUpdate={resetToFirstPageOfResults} />
          {planType !== PlanType.PDP && (
            <CategoryFilterGroup
              onFilterUpdate={resetToFirstPageOfResults}
              availableCategories={availableCategories}
            />
          )}

          <div className="PlanFilters__carriersAndStars">
            <StarRatingsFilter onStarRatingUpdate={resetToFirstPageOfResults} />
            <CarrierFilter onCarrierUpdate={resetToFirstPageOfResults} />
            {planType !== PlanType.PDP && (
              <DrugCoverageFilter
                onDrugCoverageUpdate={resetToFirstPageOfResults}
              />
            )}
          </div>
          {tmpSeniorSavingsModel &&
            (showSeniorSavingsModelFilter ||
              (seniorSavingsModelFilter && !showSeniorSavingsModelFilter)) && (
              <>
                <Choice
                  type="checkbox"
                  id="senior-savings-model"
                  name="senior-savings-model"
                  defaultChecked={seniorSavingsModelFilter}
                  checked={seniorSavingsModelFilter}
                  onChange={(e: React.FormEvent<HTMLInputElement>): void => {
                    if (e.currentTarget.checked) {
                      sendTealiumEvent(
                        "Find Plans - Available Plans",
                        "Insulin Savings Program"
                      );
                    }

                    dispatch({
                      type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                      payload: {
                        ...searchResultsFilters,
                        seniorSavingsModelFilter: e.currentTarget.checked,
                      },
                    });

                    resetToFirstPageOfResults();
                  }}
                  value=""
                >
                  {t("filter_labels.senior_savings_model")}

                  <HelpDrawerToggle
                    helpDrawerOpen={seniorSavingsModelDrawerOpen}
                    showDrawer={(): void => {
                      setSeniorSavingsModelDrawerOpen(true);
                      sendTealiumEvent(
                        "Find Plans - Available Plans",
                        "Insulin Savings Program - What's this?"
                      );
                    }}
                  >
                    {t("whats_this")}
                  </HelpDrawerToggle>
                </Choice>

                {seniorSavingsModelDrawerOpen && (
                  <div className="mct-c-help-drawer">
                    <HelpDrawer
                      onCloseClick={(): void =>
                        setSeniorSavingsModelDrawerOpen(false)
                      }
                      heading={t("filter_labels.senior_savings_model")}
                      closeButtonText={<Close />}
                    >
                      <p>
                        {t(
                          "search_results_page.filters.senior_savings_model.help"
                        )}
                      </p>
                    </HelpDrawer>
                  </div>
                )}
              </>
            )}
        </div>
        <div className="PlanFilters__buttons">
          <Button
            variation="primary"
            onClick={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - Available Plans", "Apply Filters");
              // istanbul ignore next: built-in functionality
              setOpen(!open);
            }}
          >
            {t("search_results_page.apply_filters")}
          </Button>
          <Button
            variation="transparent"
            className="ds-c-button--primary-alt e2e-clear-filters ds-u-padding--0 ds-u-margin-left--2"
            onClick={(): void => {
              sendTealiumEvent("Find Plans - Available Plans", "Clear");
              clearAllFilters();
            }}
            aria-label={t("filter_clear_all.aria")}
          >
            {t("search_results_page.clear")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlanFiltersContainer;
